import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

// Notification context for global state
const NotificationContext = React.createContext();

export const useNotifications = () => {
  const context = React.useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

// Notification Provider Component
export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const addNotification = useCallback((notification) => {
    const id = Date.now() + Math.random();
    const newNotification = {
      id,
      type: 'info', // info, success, warning, error
      title: 'Notification',
      message: '',
      duration: 4000,
      ...notification
    };

    setNotifications(prev => [...prev, newNotification]);

    // Auto remove after duration
    if (newNotification.duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, newNotification.duration);
    }

    return id;
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Convenience methods
  const showSuccess = useCallback((title, message, options = {}) => {
    return addNotification({ type: 'success', title, message, ...options });
  }, [addNotification]);

  const showError = useCallback((title, message, options = {}) => {
    return addNotification({ type: 'error', title, message, duration: 6000, ...options });
  }, [addNotification]);

  const showWarning = useCallback((title, message, options = {}) => {
    return addNotification({ type: 'warning', title, message, ...options });
  }, [addNotification]);

  const showInfo = useCallback((title, message, options = {}) => {
    return addNotification({ type: 'info', title, message, ...options });
  }, [addNotification]);

  // Custom confirm dialog
  const showConfirm = useCallback((title, message, onConfirm, onCancel, options = {}) => {
    const id = addNotification({
      type: 'confirm',
      title,
      message,
      actions: {
        confirm: onConfirm,
        cancel: onCancel || (() => {})
      },
      duration: 0, // Don't auto-close confirm dialogs
      ...options
    });
    return id;
  }, [addNotification]);

  const context = {
    notifications,
    addNotification,
    removeNotification,
    clearAllNotifications,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showConfirm
  };

  return (
    <NotificationContext.Provider value={context}>
      {children}
      <NotificationContainer />
    </NotificationContext.Provider>
  );
};

// Notification Container Component
const NotificationContainer = () => {
  const { notifications, removeNotification } = useNotifications();

  return (
    <div className="fixed top-4 right-4 z-[9999] space-y-3 pointer-events-none">
      <AnimatePresence>
        {notifications.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </AnimatePresence>
    </div>
  );
};

// Individual Notification Component
const NotificationItem = ({ notification, onClose }) => {
  const getIcon = () => {
    switch (notification.type) {
      case 'success':
        return <SafeIcon name="CheckCircle" className="w-5 h-5" />;
      case 'error':
        return <SafeIcon name="XCircle" className="w-5 h-5" />;
      case 'warning':
        return <SafeIcon name="AlertTriangle" className="w-5 h-5" />;
      case 'confirm':
        return <SafeIcon name="HelpCircle" className="w-5 h-5" />;
      default:
        return <SafeIcon name="Info" className="w-5 h-5" />;
    }
  };

  const getColors = () => {
    switch (notification.type) {
      case 'success':
        return 'bg-apache text-white border-apache/30';
      case 'error':
        return 'bg-red-500 text-white border-red-500/30';
      case 'warning':
        return 'bg-gold text-brown border-gold/30';
      case 'confirm':
        return 'bg-brown text-white border-brown/30';
      default:
        return 'bg-blue-500 text-white border-blue-500/30';
    }
  };

  const handleConfirm = () => {
    if (notification.actions?.confirm) {
      notification.actions.confirm();
    }
    onClose();
  };

  const handleCancel = () => {
    if (notification.actions?.cancel) {
      notification.actions.cancel();
    }
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 300, scale: 0.8 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 300, scale: 0.8 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      className={`pointer-events-auto max-w-sm rounded-2xl shadow-apple-hover border backdrop-blur-xl ${getColors()}`}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            {getIcon()}
          </div>
          
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-sm">{notification.title}</h4>
            {notification.message && (
              <p className="text-sm opacity-90 mt-1">{notification.message}</p>
            )}
          </div>

          {notification.type !== 'confirm' && (
            <button
              onClick={onClose}
              className="flex-shrink-0 opacity-70 hover:opacity-100 transition-opacity"
            >
              <SafeIcon name="X" className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Confirm Dialog Actions */}
        {notification.type === 'confirm' && (
          <div className="flex gap-3 mt-4">
            <motion.button
              onClick={handleConfirm}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex-1 px-3 py-2 bg-white/20 rounded-lg text-sm font-medium hover:bg-white/30 transition-colors"
            >
              Confirm
            </motion.button>
            <motion.button
              onClick={handleCancel}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex-1 px-3 py-2 bg-black/20 rounded-lg text-sm font-medium hover:bg-black/30 transition-colors"
            >
              Cancel
            </motion.button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Global alert/confirm replacer functions
export const createGlobalAlert = (showError) => {
  window.alert = (message) => {
    showError('Alert', message.toString());
  };
};

export const createGlobalConfirm = (showConfirm) => {
  window.confirm = (message) => {
    let result = false;
    const promise = new Promise((resolve) => {
      showConfirm(
        'Confirmation',
        message.toString(),
        () => {
          result = true;
          resolve(true);
        },
        () => {
          result = false;
          resolve(false);
        }
      );
    });
    return promise;
  };
};

export default NotificationProvider;